import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);

    document.addEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.current = 0;
    this.play();
  }

  disconnectedCallback() {
    this.suspend();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.#handleFocusOut);

    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    super.disconnectedCallback();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * @param {number | undefined} interval Interval in milliseconds.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();

    if (!interval || (this.refs.slides ?? []).length < 2) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (
        document.hidden ||
        this.matches(':hover') ||
        this.contains(document.activeElement)
      ) {
        return;
      }

      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    this.toggleAttribute('paused', value);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused || document.hidden) return;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const seconds = Number.parseInt(
      this.getAttribute('autoplay') ?? '',
      10
    );

    if (!Number.isFinite(seconds) || seconds <= 0) return undefined;

    return seconds * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    this.#current = ((value % slides.length) + slides.length)
      % slides.length;

    slides.forEach((slide, index) => {
      slide.setAttribute(
        'aria-hidden',
        String(index !== this.#current)
      );
    });
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      this.contains(event.relatedTarget)
    ) {
      return;
    }

    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define(
    'announcement-bar-component',
    AnnouncementBar
  );
}
